// ---------------------------------------------------------------------------
// Builds an EvalHid project from scratch, in memory — without going through
// the Access/Jet format (there's no JS library to WRITE a .ehd, only to read
// one). The flow mirrors the original's (see PLAN_VIABILIDAD.md, point 10,
// and the forms PideInfoNuevo.vb/InfoCuencas.vb/Zonas.vb/AsignacionZonas.vb/
// Acuiferos.vb/AsigPDS.vb/AsigAcuif.vb/InfoAgregarSubCuenc.vb/GIS.vb):
//
//   1. Import sub-basins from a shapefile (name + area from the .dbf).
//   2. Create zones (each with a rainfall-runoff or snow model, and its
//      default parameters) and assign them to the sub-basins in bulk.
//   3. Create outlet points and aquifers.
//   4. Assign topology in bulk (surface outlet and outlet aquifer, each one
//      separately — like AsigPDS.vb/AsigAcuif.vb).
//   5. Load precipitation/ETP (required) and temperature/pumping (optional).
//
// Bulk assignments (zone, outlet, aquifer) can be done from a list
// (checkboxes) or by clicking on the map if the project has a shapefile —
// in the original (GIS.vb) both mechanisms share the same selection, they
// sync on their own; here too (see AssignmentMap.tsx).
//
// The original (Modelos.vb) lets a sub-basin's model be assigned separately
// from its zone, without checking that they match — that's a real
// inconsistency of the original, not a rule to replicate. Here the
// sub-basin's model (modelTypeCode/snowModelTypeCode) is always DERIVED
// from the zone assigned to it (see assignZoneInBulk): a single place where
// it's decided, with no redundancy that could get out of sync.
//
// Every function is pure: it takes an EvalHidProject and returns a new one,
// like a reducer — so the UI could implement undo/redo simply by keeping
// previous states, if that's ever needed.
//
// This module is a barrel: the actual entity CRUD lives in
// subBasinEntities.ts/zoneEntities.ts/outletPointEntities.ts/
// aquiferEntities.ts (one per domain entity), with the GIS-import dedup
// logic they all share in geoImport.ts and other cross-entity helpers
// (nextId, uniqueName, updateSubBasins...) in entityHelpers.ts. Split
// 2026-08 — a single 1172-line file covering 4 unrelated entity types had
// become the repo's highest-fan-in module and its most likely merge-conflict
// point (2026-08-24 structure/maintainability review). Every caller keeps
// importing from "./projectEntities.ts" unchanged; only the internal
// organization moved.
// ---------------------------------------------------------------------------
import { MODEL_TYPES, PARAMETER_DEFINITIONS, STATE_VARIABLE_DEFINITIONS } from "./baseTemplate.ts";
import { DEFAULT_UNIT_OPTIONS } from "./units.ts";
import type { EvalHidProject } from "./types.ts";

export { NO_MODEL } from "./entityHelpers.ts";
export type { GisColumnMapping } from "./geoImport.ts";
export * from "./zoneEntities.ts";
export * from "./outletPointEntities.ts";
export * from "./aquiferEntities.ts";
export * from "./subBasinEntities.ts";

/** Empty project with EvalHid's fixed catalog already loaded (see baseTemplate.ts). */
export function emptyProject(): EvalHidProject {
  return {
    subBasins: [],
    aquifers: [],
    outletPoints: [],
    zones: [],
    modelTypes: MODEL_TYPES,
    parameterDefinitions: PARAMETER_DEFINITIONS,
    parameterValues: [],
    stateVariableDefinitions: STATE_VARIABLE_DEFINITIONS,
    stateVariableValues: [],
    simulationFiles: [],
    gisLayers: [],
    dateRanges: [],
    scenarioElements: [],
    version: null,
    unitOptions: DEFAULT_UNIT_OPTIONS,
    seriesScale: "daily",
    untypedTables: new Map(),
  };
}
